// src/pages/CustomFields/CustomFields.tsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import { useDispatch } from "react-redux";
import CollapseHeader from "../../../core/common/collapse-header";
import { all_routes } from "../../router/all_routes";
import { apiService } from "../../../services/api";
import { CustomField, CreateCustomFieldData } from '../../../types/camposPersonalizados';
import { Funnel, Stage } from '../../../types/funnels';
import FunnelSelector from '../deals/funnelSelector';
import { useFunnels } from "../../../hooks/useFunnels";
// Importar Bootstrap CSS e JS
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import { Offcanvas, Modal } from 'bootstrap';
import Select from 'react-select';
import { ProtectedComponent } from "../../../components/ProtectedComponent";

interface TableColumn {
  title: string;
  dataIndex?: string;
  render?: (text: any, record: CustomField) => React.ReactNode;
  sorter?: (a: CustomField, b: CustomField) => number;
}

interface SelectOption {
  value: string | number;
  label: string;
}

interface IFormData {
  funnel: SelectOption | null;
  funnelStage: SelectOption | null;
}

export const CamposPersonalizados: React.FC = () => {
  const dispatch = useDispatch();

  // Hook de funis
  const {
    funnels,
    loading: funilsLoading,
    error: funilsError,
    selectedFunnel,
    setSelectedFunnel,
    refetchFunnels
  } = useFunnels();

  // Estados principais
  const [fields, setFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingFieldId, setDeletingFieldId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filteredFields, setFilteredFields] = useState<CustomField[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [formFunnel, setFormFunnel] = useState<Funnel | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showDeleteSuccessModal, setShowDeleteSuccessModal] = useState(false);
  const [isLoadingFields, setIsLoadingFields] = useState(false);

  // Estados de edição
  const [editingField, setEditingField] = useState<CustomField | null>(null);
  const [newField, setNewField] = useState<CreateCustomFieldData>({
    titulo: '',
    tipo_campo: 'texto',
    id_funil: selectedFunnel?.id || 0,
    obrigatorio: false,
    exibir_detalhes: false,
    exibir_criacao: false
  });
  const [formData, setFormData] = useState<IFormData>({
    funnel: null,
    funnelStage: null
  });

  // Efeito para inicializar a lista filtrada
  useEffect(() => {
    setFilteredFields(fields);
  }, [fields]);

  // Função para filtrar campos quando o texto de busca muda (com debounce)
  useEffect(() => {
    setIsSearching(true);
    const debounceTimeout = setTimeout(() => {
      if (!searchText.trim()) {
        setFilteredFields(fields);
        setIsSearching(false);
        return;
      }

      const searchTerms = searchText.toLowerCase().trim().split(' ');

      const filtered = fields.filter(field => {
        // Campos a serem pesquisados
        const searchableFields = [
          field.titulo,
          field.descricao,
          field.tipo_campo,
          field.ativo ? 'inativo' : 'ativo'
        ].filter(Boolean).map(field => field?.toLowerCase() || '');

        // Verificar se todos os termos de pesquisa estão presentes em pelo menos um dos campos
        return searchTerms.every(term =>
          searchableFields.some(field => field.includes(term))
        );
      });

      setFilteredFields(filtered);
      setIsSearching(false);
    }, 300); // 300ms de delay para debounce

    return () => clearTimeout(debounceTimeout);
  }, [searchText, fields]);

  // Função para limpar texto de pesquisa
  const handleClearSearch = () => {
    setSearchText('');
  };

  // Colunas da tabela
  const columns: TableColumn[] = [
    {
      title: "Nome do Campo",
      render: (_, record) => (
        <div>
          <div className="fw-bold">{record.titulo}</div>
          {record.descricao && (
            <small className="text-muted">{record.descricao}</small>
          )}
        </div>
      ),
      sorter: (a, b) => a.titulo.localeCompare(b.titulo),
    },
    {
      title: "Tipo",
      dataIndex: "tipo_campo",
      render: (tipo) => (
        <div className="badge bg-primary">
          {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
        </div>
      ),
    },
    {
      title: "Funil/Etapa",
      render: (_, record) => {
        const funnel = funnels?.find(f => f.id === record.id_funil);
        const stage = funnel?.etapas?.find(e => e.id === record.id_etapa);
        return (
          <div>
            <div>{funnel?.nome || 'Funil não encontrado'}</div>
            {stage ? (
              <small className="text-muted">
                Etapa: {stage.nome}
              </small>
            ) : (
              <small className="text-muted">
                Todas as etapas
              </small>
            )}
          </div>
        );
      },
    },
    {
      title: "Obrigatório",
      dataIndex: "obrigatorio",
      render: (obrigatorio) => (
        <span className={`badge ${obrigatorio ? 'bg-danger' : 'bg-secondary'}`}>
          {obrigatorio ? 'Sim' : 'Não'}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "ativo",
      render: (ativo) => (
        <span className={`badge ${ativo ? 'bg-danger' : 'bg-success'}`}>
          {ativo ? 'Inativo' : 'Ativo'}
        </span>
      ),
    },
    {
      title: "Ações",
      render: (_, record) => (
        <div>
          <ProtectedComponent module="CONFIG_CAMPOS_PERSONALIZADOS" action="EDITAR">
            <Link
              to="#"
              className="dropdown-item"
              data-bs-toggle="offcanvas"
              data-bs-target="#offcanvas_edit"
              onClick={() => handleEdit(record)}
            >
              <i className="ti ti-edit text-blue"></i> Editar
            </Link>
          </ProtectedComponent>
          <ProtectedComponent module="CONFIG_CAMPOS_PERSONALIZADOS" action="EXCLUIR">
            <Link
              to="#"
              className="dropdown-item"
              data-bs-toggle="modal"
              data-bs-target="#delete_field"
              onClick={() => handlePrepareDelete(record)}
            >
              <i className="ti ti-trash text-danger"></i> Excluir
            </Link>
          </ProtectedComponent>
        </div>
      ),
    },
  ];

  const handleFunnelChange = (option: SelectOption | null) => {
    if (option) {
      const funnel = funnels.find(f => f.id === option.value);
      if (funnel) {
        setSelectedFunnel(funnel);
        setFormData(prev => ({
          ...prev,
          funnel: option,
          funnelStage: null // Reseta a etapa quando o funil muda
        }));

        // Atualiza o ID do funil no novo campo sem recarregar
        setNewField(prev => ({
          ...prev,
          id_funil: funnel.id
        }));
      }
    }
  };

  // Efeitos
  useEffect(() => {
    if (selectedFunnel) {
      setNewField(prev => ({
        ...prev,
        id_funil: selectedFunnel.id
      }));
      loadFields();
    }
  }, [selectedFunnel]);

  // Funções de carregamento
  const loadFields = async () => {
    if (!selectedFunnel) return;

    try {
      setLoading(true);
      const response = await apiService.getCustomFields(selectedFunnel.id);
      setFields(response.data);
      setError(null);
    } catch (error) {
      setError('Erro ao carregar campos personalizados');
      console.error('Erro:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (name: keyof IFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  function isStageWithId(stage: Stage): stage is Stage & { id: number } {
    return typeof stage.id === 'number';
  }

  // Handlers
  const handleEdit = (field: CustomField) => {
    setEditingField(field);
  };

  const handlePrepareDelete = (field: CustomField) => {
    setDeletingFieldId(field.id);
  };

  const handleStageChange = (option: SelectOption | null) => {
    handleInputChange('funnelStage', option);

    // Atualiza o id_etapa no newField
    setNewField(prev => ({
      ...prev,
      id_etapa: option ? Number(option.value) : undefined // Se nenhuma etapa selecionada, remove o id_etapa
    }));
  };

  const handleCancelEdit = () => {
    setEditingField(null);
  };

  const handleCancelNew = () => {
    setNewField({
      titulo: '',
      tipo_campo: 'texto',
      id_funil: selectedFunnel?.id || 0,
      obrigatorio: false,
      exibir_detalhes: false,
      exibir_criacao: false
    });
  };

  const limparModal = (idModal: string) => {
    const elementoModal = document.getElementById(idModal);
    const instanciaModal = elementoModal ? Modal.getInstance(elementoModal) : null;

    if (instanciaModal) {
      instanciaModal.hide();
    }

    const executarLimpeza = () => {
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('padding-right');
      document.body.classList.remove('modal-open', 'offcanvas-open');

      const backdrops = document.querySelectorAll('.modal-backdrop, .offcanvas-backdrop');
      backdrops.forEach(backdrop => backdrop.remove());
    };

    if (elementoModal?.classList.contains('show')) {
      elementoModal.addEventListener('hidden.bs.modal', () => {
        executarLimpeza();
      }, { once: true });
    } else {
      executarLimpeza();
    }
  };

  // Handler para Criar Campo
  const handleCreateField = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);
      const response = await apiService.createCustomField({
        ...newField,
        id_funil: selectedFunnel?.id || 0,
        ...(formData.funnelStage ? { id_etapa: Number(formData.funnelStage.value) } : {})
      });

      if (response.success) {
        // Fecha offcanvas
        const offcanvasElement = document.getElementById('offcanvas_field');
        if (offcanvasElement) {
          const offcanvasInstance = Offcanvas.getInstance(offcanvasElement);
          if (offcanvasInstance) {
            offcanvasInstance.hide();
          }
        }

        handleCancelNew();
        await loadFields();

        // Mostra modal de sucesso após um pequeno delay
        setTimeout(() => {
          const successModal = document.getElementById('success_modal');
          if (successModal) {
            const modalInstance = new Modal(successModal);
            modalInstance.show();
          }
        }, 300);
      }
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateField = async () => {
    if (!editingField?.id) return;

    try {
      setIsSubmitting(true);
      await apiService.updateCustomField(editingField.id, editingField);
      await loadFields();

      // Fecha o offcanvas
      const offcanvasElement = document.getElementById('offcanvas_edit');
      if (offcanvasElement) {
        const offcanvasInstance = Offcanvas.getInstance(offcanvasElement);
        if (offcanvasInstance) {
          offcanvasInstance.hide();
        }
      }

      // Mostra modal de sucesso
      setTimeout(() => {
        const successModal = document.getElementById('success_edit_modal');
        if (successModal) {
          const modalInstance = new Modal(successModal);
          modalInstance.show();
        }
      }, 300);

      handleCancelEdit();
    } catch (error) {
      console.error('Erro ao atualizar campo:', error);
      alert('Erro ao atualizar o campo. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler para Deletar Campo
  const handleDeleteField = async () => {
    if (!deletingFieldId) return;

    try {
      setIsDeleting(true);
      const response = await apiService.deleteCustomField(deletingFieldId);

      if (response.success) {
        await loadFields();

        // Fecha o modal de confirmação de delete
        const deleteModal = document.getElementById('delete_field');
        if (deleteModal) {
          const modalInstance = Modal.getInstance(deleteModal);
          if (modalInstance) {
            modalInstance.hide();
          }
        }

        // Mostra modal de sucesso após um pequeno delay
        setTimeout(() => {
          const successModal = document.getElementById('success_delete_modal');
          if (successModal) {
            const modalInstance = new Modal(successModal);
            modalInstance.show();
          }
        }, 300);
      }
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      }
    } finally {
      setIsDeleting(false);
      setDeletingFieldId(null);
    }
  };

  // Renderização condicional para loading e erro
  if (funilsLoading || loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (funilsError || error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body">
                  <div className="alert alert-danger" role="alert">
                    {funilsError || error}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Cabeçalho da Página */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-4">
                    <h4 className="page-title">
                      Campos Personalizados
                      <span className="count-title">{filteredFields.length} campos</span>
                    </h4>
                  </div>
                  <div className="col-8 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col-sm-4">
                      <div className="search-input position-relative">
                        <div className="icon-form mb-3 mb-sm-0">
                          <span className="form-icon">
                            {isSearching ? (
                              <i className="spinner-border spinner-border-sm" role="status" />
                            ) : (
                              <i className="ti ti-search" />
                            )}
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Pesquisar Campo"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                          />
                          {searchText && (
                            <button
                              className="btn btn-sm position-absolute"
                              style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}
                              onClick={handleClearSearch}
                              type="button"
                            >
                              <i className="ti ti-x" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-4">
                      <select
                        className="form-select"
                        value={selectedFunnel?.id || ''}
                        onChange={(e) => setSelectedFunnel(
                          funnels.find(funnel => funnel.id === Number(e.target.value)) || null
                        )}
                      >
                        <option value="">Todos os Funis</option>
                        {funnels?.map((funnel: Funnel) => (
                          <option key={funnel.id} value={funnel.id}>
                            {funnel.nome}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-4 text-sm-end mt-3 mt-sm-0">
                      <ProtectedComponent module="CONFIG_CAMPOS_PERSONALIZADOS" action="CRIAR">
                        <Link
                          to="#"
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_field"
                        >
                          <i className="ti ti-plus me-2" />
                          Adicionar Campo
                        </Link>
                      </ProtectedComponent>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  {/* Filtros e Ações */}
                  <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2 mb-4">
                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      {/* Botão Ordenar */}
                      <div className="dropdown me-2">
                        <Link to="#" className="dropdown-toggle" data-bs-toggle="dropdown">
                          <i className="ti ti-sort-ascending-2 me-2" />
                          Ordenar
                        </Link>
                        <div className="dropdown-menu">
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Nome A-Z
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Nome Z-A
                          </Link>
                        </div>
                      </div>

                      {/* Botão Exportar */}
                      <div className="dropdown">
                        <Link to="#" className="dropdown-toggle" data-bs-toggle="dropdown">
                          <i className="ti ti-package-export me-2" />
                          Exportar
                        </Link>
                        <div className="dropdown-menu dropdown-menu-end">
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-file-type-pdf text-danger me-1" />
                            Exportar como PDF
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-file-type-xls text-green me-1" />
                            Exportar como Excel
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tabela de Campos */}
                  <div className="table-responsive custom-table">
                    {!isSearching && searchText && filteredFields.length === 0 ? (
                      <div className="text-center py-5">
                        <div className="mb-3">
                          <i className="ti ti-search-off fs-3 text-muted"></i>
                        </div>
                        <h5 className="text-muted">Nenhum campo encontrado</h5>
                        <p className="text-muted">Sua pesquisa por "{searchText}" não retornou resultados.</p>
                        <button
                          className="btn btn-outline-primary mt-2"
                          onClick={handleClearSearch}
                        >
                          <i className="ti ti-x me-1"></i>
                          Limpar pesquisa
                        </button>
                      </div>
                    ) : (
                      <Table
                        dataSource={filteredFields.map(field => ({
                          ...field,
                          key: field.id.toString()
                        }))}
                        columns={columns}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Offcanvas Adicionar Novo Campo */}
      <div className="offcanvas offcanvas-end offcanvas-large" tabIndex={-1} id="offcanvas_field">
        <div className="offcanvas-header border-bottom">
          <h4>Adicionar Novo Campo</h4>
          <button type="button" className="btn-close" data-bs-dismiss="offcanvas">
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          <form onSubmit={handleCreateField}>
            <div className="mb-3">
              <label className="col-form-label">
                Título do Campo <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={newField.titulo}
                onChange={(e) => setNewField(prev => ({
                  ...prev,
                  titulo: e.target.value
                }))}
                required
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">
                Funil <span className="text-danger">*</span>
              </label>
              <Select
                name="funnel"
                className="select2"
                options={funnels.map(f => ({
                  value: f.id,
                  label: f.nome
                }))}
                value={formData.funnel}
                onChange={(option) => {
                  if (option) {
                    const funnel = funnels.find(f => f.id === option.value);

                    // Atualiza apenas os estados do formulário
                    handleInputChange('funnel', option);
                    handleInputChange('funnelStage', null);

                    // Atualiza o funil apenas no contexto do formulário
                    if (funnel) {
                      setFormFunnel(funnel);
                      setNewField(prev => ({
                        ...prev,
                        id_funil: funnel.id
                      }));
                    }
                  }
                }}
                placeholder="Selecione o funil"
                classNamePrefix="react-select"
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">
                Etapa do Funil <span className="text-danger">*</span>
              </label>
              <Select
                name="funnelStage"
                className="select2"
                options={
                  formFunnel?.etapas
                    ?.filter(isStageWithId)
                    .map(e => ({
                      value: e.id,
                      label: e.nome
                    } as SelectOption)) || []
                }
                value={formData.funnelStage}
                onChange={handleStageChange}
                placeholder="Selecione a etapa"
                isDisabled={!formFunnel}
                classNamePrefix="react-select"
              />
              <small className="text-muted">
                Se nenhuma etapa for selecionada, o campo será exibido em todas as etapas do funil
              </small>
            </div>


            <div className="mb-3">
              <label className="col-form-label">
                Tipo de Campo <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                value={newField.tipo_campo}
                onChange={(e) => setNewField(prev => ({
                  ...prev,
                  tipo_campo: e.target.value as CustomField['tipo_campo']
                }))}
                required
              >
                <option value="texto">Texto</option>
                <option value="numero">Número</option>
                <option value="monetario">Monetário</option>
                <option value="data">Data</option>
                <option value="hora">Hora</option>
                <option value="select">Seleção Única</option>
                <option value="multiselect">Seleção Múltipla</option>
                <option value="checkbox">Checkbox</option>
                <option value="radio">Radio</option>
                <option value="textarea">Área de Texto</option>
                <option value="email">Email</option>
                <option value="telefone">Telefone</option>
                <option value="cpf">CPF</option>
                <option value="cnpj">CNPJ</option>
                <option value="porcentagem">Porcentagem</option>
              </select>
            </div>

            {(newField.tipo_campo === 'select' || newField.tipo_campo === 'multiselect' || newField.tipo_campo === 'radio') && (
              <div className="mb-3">
                <label className="col-form-label">
                  Opções <span className="text-danger">*</span>
                </label>
                <div className="options-container">
                  {newField.opcoes?.map((opcao, index) => (
                    <div key={index} className="d-flex mb-2 gap-2">
                      <input
                        type="text"
                        className="form-control"
                        value={opcao.label}
                        onChange={(e) => {
                          const newOpcoes = [...(newField.opcoes || [])];
                          newOpcoes[index].label = e.target.value;
                          setNewField(prev => ({
                            ...prev,
                            opcoes: newOpcoes
                          }));
                        }}
                        placeholder="Label da opção"
                      />
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => {
                          const newOpcoes = newField.opcoes?.filter((_, i) => i !== index);
                          setNewField(prev => ({
                            ...prev,
                            opcoes: newOpcoes
                          }));
                        }}
                      >
                        <i className="ti ti-trash" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm mt-2"
                    onClick={() => {
                      const newOpcoes = [...(newField.opcoes || [])];
                      newOpcoes.push({ id: newOpcoes.length + 1, label: '' });
                      setNewField(prev => ({
                        ...prev,
                        opcoes: newOpcoes
                      }));
                    }}
                  >
                    <i className="ti ti-plus me-1" />
                    Adicionar Opção
                  </button>
                </div>
              </div>
            )}

            <div className="mb-3">
              <label className="col-form-label">Descrição</label>
              <textarea
                className="form-control"
                value={newField.descricao || ''}
                onChange={(e) => setNewField(prev => ({
                  ...prev,
                  descricao: e.target.value
                }))}
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">Placeholder</label>
              <input
                type="text"
                className="form-control"
                value={newField.placeholder || ''}
                onChange={(e) => setNewField(prev => ({
                  ...prev,
                  placeholder: e.target.value
                }))}
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">Valor Padrão</label>
              <input
                type="text"
                className="form-control" 
                value={newField.valor_padrao || ''}
                onChange={(e) => setNewField(prev => ({
                  ...prev,
                  valor_padrao: e.target.value
                }))}
              />
            </div>

            {(newField.tipo_campo === 'numero' || newField.tipo_campo === 'monetario' || newField.tipo_campo === 'porcentagem') && (
              <div className="row">
                <div className="col-6 mb-3">
                  <label className="col-form-label">Valor Mínimo</label>
                  <input
                    type="number"
                    className="form-control"
                    value={newField.validacoes?.min || ''}
                    onChange={(e) => setNewField(prev => ({
                      ...prev,
                      validacoes: {
                        ...prev.validacoes,
                        min: Number(e.target.value)
                      }
                    }))}
                  />
                </div>
                <div className="col-6 mb-3">
                  <label className="col-form-label">Valor Máximo</label>
                  <input
                    type="number"
                    className="form-control"
                    value={newField.validacoes?.max || ''}
                    onChange={(e) => setNewField(prev => ({
                      ...prev,
                      validacoes: {
                        ...prev.validacoes,
                        max: Number(e.target.value)
                      }
                    }))}
                  />
                </div>
              </div>
            )}

            <div className="mb-3">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="obrigatorio"
                  checked={newField.obrigatorio}
                  onChange={(e) => setNewField(prev => ({
                    ...prev,
                    obrigatorio: e.target.checked
                  }))}
                />
                <label className="form-check-label" htmlFor="obrigatorio">
                  Campo Obrigatório
                </label>
              </div>
            </div>

            <div className="mb-3">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="exibir_detalhes"
                  checked={newField.exibir_detalhes ?? true}
                  onChange={(e) => setNewField(prev => ({
                    ...prev,
                    exibir_detalhes: e.target.checked
                  }))}
                />
                <label className="form-check-label" htmlFor="exibir_detalhes">
                  Exibir em detalhes da Oportunidade
                </label>
              </div>
            </div>

            <div className="mb-3">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="exibir_criacao"
                  checked={newField.exibir_criacao ?? true}
                  onChange={(e) => setNewField(prev => ({
                    ...prev,
                    exibir_criacao: e.target.checked
                  }))}
                />
                <label className="form-check-label" htmlFor="exibir_criacao">
                  Exibir em Criação/Edição de Oportunidade
                </label>
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-end">
              <button
                type="button"
                className="btn btn-light me-2"
                data-bs-dismiss="offcanvas"
                onClick={handleCancelNew}
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Criando...
                  </>
                ) : (
                  'Criar Campo'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Offcanvas Editar Campo */}
      <div className="offcanvas offcanvas-end offcanvas-large" tabIndex={-1} id="offcanvas_edit">
        <div className="offcanvas-header border-bottom">
          <h4>Editar Campo</h4>
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="offcanvas"
            onClick={handleCancelEdit}
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          <form onSubmit={(e) => {
            e.preventDefault();
            handleUpdateField();
          }}>
            <div className="mb-3">
              <label className="col-form-label">
                Título do Campo <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={editingField?.titulo || ''}
                onChange={(e) => setEditingField(prev => prev ? {
                  ...prev,
                  titulo: e.target.value
                } : null)}
                required
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">
                Tipo de Campo <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                value={editingField?.tipo_campo || ''}
                onChange={(e) => setEditingField(prev => prev ? {
                  ...prev,
                  tipo_campo: e.target.value as CustomField['tipo_campo']
                } : null)}
                required
              >
                <option value="texto">Texto</option>
                <option value="numero">Número</option>
                <option value="monetario">Monetário</option>
                <option value="data">Data</option>
                <option value="hora">Hora</option>
                <option value="select">Seleção Única</option>
                <option value="multiselect">Seleção Múltipla</option>
                <option value="checkbox">Checkbox</option>
                <option value="radio">Radio</option>
                <option value="textarea">Área de Texto</option>
                <option value="email">Email</option>
                <option value="telefone">Telefone</option>
                <option value="cpf">CPF</option>
                <option value="cnpj">CNPJ</option>
                <option value="porcentagem">Porcentagem</option>
              </select>
            </div>

            {(editingField?.tipo_campo === 'select' || editingField?.tipo_campo === 'multiselect' || editingField?.tipo_campo === 'radio') && (
              <div className="mb-3">
                <label className="col-form-label">
                  Opções <span className="text-danger">*</span>
                </label>
                <div className="options-container">
                  {editingField.opcoes?.map((opcao, index) => (
                    <div key={index} className="d-flex mb-2 gap-2">
                      <input
                        type="text"
                        className="form-control"
                        value={opcao.label}
                        onChange={(e) => {
                          const newOpcoes = [...(editingField.opcoes || [])];
                          newOpcoes[index].label = e.target.value;
                          setEditingField(prev => prev ? {
                            ...prev,
                            opcoes: newOpcoes
                          } : null);
                        }}
                        placeholder="Label da opção"
                      />
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => {
                          const newOpcoes = editingField.opcoes?.filter((_, i) => i !== index);
                          setEditingField(prev => prev ? {
                            ...prev,
                            opcoes: newOpcoes
                          } : null);
                        }}
                      >
                        <i className="ti ti-trash" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm mt-2"
                    onClick={() => {
                      const newOpcoes = [...(editingField.opcoes || [])];
                      newOpcoes.push({ id: newOpcoes.length + 1, label: '' });
                      setEditingField(prev => prev ? {
                        ...prev,
                        opcoes: newOpcoes
                      } : null);
                    }}
                  >
                    <i className="ti ti-plus me-1" />
                    Adicionar Opção
                  </button>
                </div>
              </div>
            )}

            <div className="mb-3">
              <label className="col-form-label">Descrição</label>
              <textarea
                className="form-control"
                value={editingField?.descricao || ''}
                onChange={(e) => setEditingField(prev => prev ? {
                  ...prev,
                  descricao: e.target.value
                } : null)}
              />
            </div>

            <div className="mb-3">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="exibir_detalhes"
                  checked={editingField?.exibir_detalhes || false}
                  onChange={(e) => setEditingField(prev => prev ? {
                    ...prev,
                    exibir_detalhes: e.target.checked
                  } : null)}
                />
                <label className="form-check-label" htmlFor="exibir_detalhes">
                  Exibir na tela de detalhes
                </label>
              </div>
            </div>

            <div className="mb-3">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="exibir_criacao"
                  checked={editingField?.exibir_criacao || false}
                  onChange={(e) => setEditingField(prev => prev ? {
                    ...prev,
                    exibir_criacao: e.target.checked
                  } : null)}
                />
                <label className="form-check-label" htmlFor="exibir_criacao">
                  Exibir na tela de criação
                </label>
              </div>
            </div>

            <div className="mb-3">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="obrigatorio"
                  checked={editingField?.obrigatorio || false}
                  onChange={(e) => setEditingField(prev => prev ? {
                    ...prev,
                    obrigatorio: e.target.checked
                  } : null)}
                />
                <label className="form-check-label" htmlFor="obrigatorio">
                  Campo Obrigatório
                </label>
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-end">
              <button
                type="button"
                className="btn btn-light me-2"
                data-bs-dismiss="offcanvas"
                onClick={handleCancelEdit}
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Salvando...
                  </>
                ) : (
                  'Salvar Alterações'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal de Confirmação de Exclusão */}
      <div className="modal fade" id="delete_field" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="text-center">
                <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                  <i className="ti ti-trash-x fs-36 text-danger" />
                </div>
                <h4 className="mb-2">Remover Campo?</h4>
                <p className="mb-0">
                  Tem certeza que deseja remover o campo personalizado selecionado?
                </p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                    disabled={isDeleting}
                    onClick={() => setDeletingFieldId(null)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDeleteField}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Excluindo...
                      </>
                    ) : (
                      'Sim, Deletar'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade custom-modal" id="success_modal" tabIndex={-1} role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="success-message text-center">
                <div className="avatar avatar-xl bg-success-light rounded-circle mb-3">
                  <i className="ti ti-check-check fs-36 text-success" />
                </div>
                <h3>Campo Criado com Sucesso!</h3>
                <p>O campo personalizado foi criado e já está disponível para uso.</p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => limparModal('success_modal')}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Sucesso - Edição */}
      <div className="modal fade custom-modal" id="success_edit_modal" tabIndex={-1} role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="success-message text-center">
                <div className="avatar avatar-xl bg-success-light rounded-circle mb-3">
                  <i className="ti ti-check-check fs-36 text-success" />
                </div>
                <h3>Campo Atualizado com Sucesso!</h3>
                <p>As alterações foram salvas com sucesso.</p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => limparModal('success_edit_modal')}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Sucesso - Exclusão */}
      <div className="modal fade custom-modal" id="success_delete_modal" tabIndex={-1} role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="success-message text-center">
                <div className="avatar avatar-xl bg-success-light rounded-circle mb-3">
                  <i className="ti ti-check-check fs-36 text-success" />
                </div>
                <h3>Campo Excluído com Sucesso!</h3>
                <p>O campo personalizado foi removido permanentemente do sistema.</p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => limparModal('success_delete_modal')}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};